import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Pill, 
  Upload, 
  Phone, 
  Search, 
  Percent,
  Truck,
  Shield,
  Clock,
  FileText,
  Heart,
  Activity
} from 'lucide-react';
import { mockPharmacyCategories, generateWhatsAppLink } from '../mock/mockData';

const Pharmacy = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [callbackForm, setCallbackForm] = useState({
    name: '',
    phone: '',
    message: ''
  });

  const handlePrescriptionUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPrescriptionFile(file);
    }
  };

  const handleSendPrescription = () => {
    // Get saved addresses
    const savedAddresses = localStorage.getItem('geeth365_addresses');
    const addresses = savedAddresses ? JSON.parse(savedAddresses) : [];
    const defaultAddress = addresses.find(addr => addr.isDefault) || addresses[0];
    
    const addressText = defaultAddress 
      ? `${defaultAddress.formattedAddress}` 
      : 'Please add delivery address in Account section';
    
    const whatsAppLink = generateWhatsAppLink('pharmacy', {
      user: user,
      address: addressText,
      items: 'Prescription medicines'
    });
    window.open(whatsAppLink, '_blank');
  };

  const handleCallbackRequest = (e) => {
    e.preventDefault();
    const whatsAppLink = `https://wa.me/917337541113?text=${encodeURIComponent(
      `Callback Request - Name: ${callbackForm.name}, Phone: ${callbackForm.phone}, Message: ${callbackForm.message}`
    )}`;
    window.open(whatsAppLink, '_blank');
    setCallbackForm({ name: '', phone: '', message: '' });
  };

  const handleCategoryClick = (category) => {
    // Get saved addresses for delivery
    const savedAddresses = localStorage.getItem('geeth365_addresses');
    const addresses = savedAddresses ? JSON.parse(savedAddresses) : [];
    const defaultAddress = addresses.find(addr => addr.isDefault) || addresses[0];
    const addressText = defaultAddress
      ? `${defaultAddress.formattedAddress}`
      : 'Please add delivery address in Account section';

    const whatsAppLink = generateWhatsAppLink('pharmacy', {
      user: user,
      address: addressText,
      items: `${category.name} medicines`
    });
    window.open(whatsAppLink, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-green-600 to-green-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Online Pharmacy
              </h1>
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <Percent className="w-6 h-6 text-green-200" />
                  <span className="text-xl">Up to 20% OFF on prescription medicines</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Truck className="w-6 h-6 text-green-200" />
                  <span className="text-xl">Free home delivery</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Shield className="w-6 h-6 text-green-200" />
                  <span className="text-xl">Cash on Delivery (COD)</span>
                </div>
              </div>
              <Badge className="bg-white text-green-700 text-lg px-4 py-2 font-semibold">
                * Subscriber benefits included
              </Badge>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1576602976047-174e57a47881"
                alt="Pharmacy"
                className="w-full h-80 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search medicines, brands, or health categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 text-lg py-3 border-gray-300 focus:border-green-500"
            />
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Prescription Upload */}
            <Card className="border-green-200">
              <CardHeader className="bg-green-50">
                <CardTitle className="flex items-center space-x-2 text-green-700">
                  <Upload className="w-5 h-5" />
                  <span>Upload Prescription</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-green-200 rounded-lg p-8 text-center hover:border-green-400 transition-colors">
                    <input
                      type="file"
                      id="prescription"
                      accept="image/*,.pdf"
                      onChange={handlePrescriptionUpload}
                      className="hidden"
                    />
                    <label htmlFor="prescription" className="cursor-pointer">
                      <FileText className="w-12 h-12 text-green-500 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">
                        {prescriptionFile ? prescriptionFile.name : 'Click to upload prescription'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Supports JPG, PNG, PDF files
                      </p>
                    </label>
                  </div>
                  
                  <div className="flex space-x-4">
                    <Button 
                      onClick={handleSendPrescription}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Send Prescription on WhatsApp
                    </Button>
                    <Button variant="outline" className="border-green-200 text-green-600">
                      Order Later
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Major Brand Partners */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Pill className="w-5 h-5 text-orange-600" />
                  <span>Major Brand Partners</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">WE DELIVER BEST BRANDED DRUGS</h2>
                  <p className="text-gray-600">Trusted pharmaceutical brands available for delivery</p>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {/* Sun Pharma */}
                  <div className="text-center p-4 bg-white rounded-lg border hover:shadow-lg transition-shadow">
                    <div className="w-16 h-16 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-bold text-sm">SUN</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">Sun Pharma</h4>
                    <Badge className="mt-2 bg-green-100 text-green-700 text-xs">Available</Badge>
                  </div>

                  {/* Cipla */}
                  <div className="text-center p-4 bg-white rounded-lg border hover:shadow-lg transition-shadow">
                    <div className="w-16 h-16 mx-auto mb-3 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-red-600 font-bold text-sm">CIPLA</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">Cipla</h4>
                    <Badge className="mt-2 bg-green-100 text-green-700 text-xs">Available</Badge>
                  </div>

                  {/* Dr. Reddy's */}
                  <div className="text-center p-4 bg-white rounded-lg border hover:shadow-lg transition-shadow">
                    <div className="w-16 h-16 mx-auto mb-3 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 font-bold text-xs">DR.R</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">Dr. Reddy's</h4>
                    <Badge className="mt-2 bg-green-100 text-green-700 text-xs">Available</Badge>
                  </div>

                  {/* Lupin */}
                  <div className="text-center p-4 bg-white rounded-lg border hover:shadow-lg transition-shadow">
                    <div className="w-16 h-16 mx-auto mb-3 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-orange-600 font-bold text-sm">LUPIN</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">Lupin</h4>
                    <Badge className="mt-2 bg-green-100 text-green-700 text-xs">Available</Badge>
                  </div>

                  {/* Aurobindo */}
                  <div className="text-center p-4 bg-white rounded-lg border hover:shadow-lg transition-shadow">
                    <div className="w-16 h-16 mx-auto mb-3 bg-teal-100 rounded-full flex items-center justify-center">
                      <span className="text-teal-600 font-bold text-xs">AURO</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">Aurobindo</h4>
                    <Badge className="mt-2 bg-green-100 text-green-700 text-xs">Available</Badge>
                  </div>

                  {/* Biocon */}
                  <div className="text-center p-4 bg-white rounded-lg border hover:shadow-lg transition-shadow">
                    <div className="w-16 h-16 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-bold text-sm">BIOCON</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">Biocon</h4>
                    <Badge className="mt-2 bg-green-100 text-green-700 text-xs">Available</Badge>
                  </div>

                  {/* Cadila */}
                  <div className="text-center p-4 bg-white rounded-lg border hover:shadow-lg transition-shadow">
                    <div className="w-16 h-16 mx-auto mb-3 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-indigo-600 font-bold text-xs">CADILA</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">Cadila</h4>
                    <Badge className="mt-2 bg-green-100 text-green-700 text-xs">Available</Badge>
                  </div>

                  {/* Glenmark */}
                  <div className="text-center p-4 bg-white rounded-lg border hover:shadow-lg transition-shadow">
                    <div className="w-16 h-16 mx-auto mb-3 bg-pink-100 rounded-full flex items-center justify-center">
                      <span className="text-pink-600 font-bold text-xs">GLEN</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">Glenmark</h4>
                    <Badge className="mt-2 bg-green-100 text-green-700 text-xs">Available</Badge>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-orange-50 rounded-lg text-center">
                  <h4 className="font-semibold text-orange-900 mb-2">All Major Brands Available</h4>
                  <p className="text-sm text-orange-700">
                    We deliver authentic medicines from all leading pharmaceutical brands across India
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Popular Medicines section removed as requested */}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Callback Request */}
            <Card className="border-blue-200">
              <CardHeader className="bg-blue-50">
                <CardTitle className="flex items-center space-x-2 text-blue-700">
                  <Phone className="w-5 h-5" />
                  <span>Request Callback</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleCallbackRequest} className="space-y-4">
                  <Input
                    placeholder="Your Name"
                    value={callbackForm.name}
                    onChange={(e) => setCallbackForm({...callbackForm, name: e.target.value})}
                    required
                  />
                  <Input
                    type="tel"
                    placeholder="Phone Number"
                    value={callbackForm.phone}
                    onChange={(e) => setCallbackForm({...callbackForm, phone: e.target.value})}
                    required
                  />
                  <Textarea
                    placeholder="Your message or medicine requirements"
                    value={callbackForm.message}
                    onChange={(e) => setCallbackForm({...callbackForm, message: e.target.value})}
                    rows={3}
                  />
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                    Request Callback
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Features */}
            <Card>
              <CardHeader>
                <CardTitle>Why Choose Our Pharmacy?</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Shield className="w-5 h-5 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900">Genuine Medicines</h4>
                      <p className="text-sm text-gray-600">Only authentic medicines from licensed suppliers</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900">Fast Delivery</h4>
                      <p className="text-sm text-gray-600">Same day delivery in most areas</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Heart className="w-5 h-5 text-red-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900">Expert Guidance</h4>
                      <p className="text-sm text-gray-600">Pharmacist consultation available</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="p-6 text-center">
                <h3 className="font-semibold text-gray-900 mb-4">Need Help?</h3>
                <div className="space-y-3">
                  <a 
                    href="https://wa.me/917337541113?text=Need help with pharmacy services"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full bg-green-600 hover:bg-green-700">
                      <Phone className="w-4 h-4 mr-2" />
                      Chat with Pharmacist
                    </Button>
                  </a>
                  <Button variant="outline" className="w-full border-orange-200 text-orange-600">
                    <Activity className="w-4 h-4 mr-2" />
                    Track Order
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pharmacy;